import { useMemo } from 'react';
import { useTimeZone } from '../user/api/use-get-user';
import { useCategoriesWithBuckets } from '../category/categories-with-buckets';
import { UNCATEGORIZED_BUCKET_CATEGORY_ID, useTransactionSummary, } from '../transaction/summary/use-transaction-summary';
import { DateTime } from 'luxon';
import { useBucketsMap } from '../bucket/api/use-get-buckets';
// Special category ID for the Archived category
const ARCHIVED_CATEGORY_ID = 'archived';
// Module-level constants
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** Type for showing trend compared to previous month */
export var TrendType;
(function (TrendType) {
    TrendType["HIGHER"] = "HIGHER";
    TrendType["LOWER"] = "LOWER";
    TrendType["NONE"] = "NONE";
})(TrendType || (TrendType = {}));
/** Enum defining the temporal status of a month */
export var MonthStatus;
(function (MonthStatus) {
    MonthStatus["PAST"] = "PAST";
    MonthStatus["CURRENT"] = "CURRENT";
    MonthStatus["FUTURE"] = "FUTURE";
})(MonthStatus || (MonthStatus = {}));
/**
 * Hook for generating income vs expense report data for a specific year
 * @param year - The year to generate the report for
 * @returns Income vs expense report data
 */
export function useIncomeVsExpenseReport(year) {
    const transactionSummary = useTransactionSummary(year);
    const categoriesWithBuckets = useCategoriesWithBuckets();
    const timeZone = useTimeZone();
    const bucketsMap = useBucketsMap();
    // Generate the report data using useMemo to avoid unnecessary recalculations
    return useMemo(() => {
        return buildIncomeVsExpenseData(year, transactionSummary, categoriesWithBuckets, timeZone, bucketsMap);
    }, [year, transactionSummary, categoriesWithBuckets, timeZone, bucketsMap]);
}
/**
 * Recalculates a category's monthly trends by summing its buckets
 * and then using finalizeSeries to compute trend + status correctly.
 * @param buckets - Active buckets in the category
 * @param year - Report year
 * @param currentYear - Current year
 * @param currentMonthIndex - Current month index (0-based)
 * @returns Recalculated monthly trends for the category
 * @internal
 */
export function recalculateCategoryMonthly(buckets, year, currentYear, currentMonthIndex) {
    // 1) Build raw sums
    const raw = initializeMonthlyValues();
    buckets.forEach((bucket) => {
        MONTHS.forEach((month) => {
            const monthData = bucket.monthly[month];
            if (monthData) {
                raw[month] += monthData.value;
            }
        });
    });
    // 2) Run your trend/status logic on the sum
    return finalizeSeries(raw, year, currentYear, currentMonthIndex);
}
/**
 * Builds the income vs expense report data structure from transaction summary
 * @param year - The year to generate the report for
 * @param transactionSummary - Pre-computed transaction summary data
 * @param categoriesWithBuckets - Categories with their buckets
 * @param timeZone - User's timezone
 * @param bucketsMap - Map of all buckets including archived ones
 * @returns Structured income vs expense report data
 * @internal
 */
export function buildIncomeVsExpenseData(year, transactionSummary, categoriesWithBuckets, timeZone, bucketsMap) {
    // Determine current month
    const currentDate = DateTime.now().setZone(timeZone);
    const currentYear = currentDate.year;
    const currentMonthIndex = currentDate.month - 1; // Convert to 0-based for our needs
    // Initialize the report data structure
    const reportData = {
        inflow: initializeInflowSection(year, currentYear, currentMonthIndex),
        outflow: initializeOutflowSection(year, currentYear, currentMonthIndex),
        summary: initializeSummarySection(year, currentYear, currentMonthIndex),
    };
    // Process payee inflows
    processPayeeInflows(reportData.inflow, transactionSummary, year, currentYear, currentMonthIndex);
    // Process category and bucket outflows
    processCategoryAndBucketOutflows(reportData.outflow, transactionSummary, categoriesWithBuckets, year, currentYear, currentMonthIndex, bucketsMap);
    // Build summary section
    buildSummarySection(reportData, year, currentYear, currentMonthIndex);
    // Filter out payees with no activity
    reportData.inflow.payees = reportData.inflow.payees.filter((payee) => Object.values(payee.monthly).some((month) => month.value !== 0));
    // Filter out categories and buckets with no activity
    const activeCategories = reportData.outflow.categories
        .map((row) => filterCategoryByActivity(row, year, currentYear, currentMonthIndex))
        .filter((category) => category !== null);
    reportData.outflow.categories = activeCategories;
    // Recalculate total outflow based on active categories only
    const totalOutflowByMonth = initializeMonthlyValues();
    activeCategories.forEach((category) => {
        MONTHS.forEach((month) => {
            if (month in category.monthly) {
                totalOutflowByMonth[month] += category.monthly[month].value;
            }
        });
    });
    reportData.outflow.total = finalizeSeries(totalOutflowByMonth, year, currentYear, currentMonthIndex);
    return reportData;
}
/**
 * Processes payee inflows from the transaction summary
 * @param inflowSection - Inflow section to populate
 * @param transactionSummary - Pre-computed transaction summary data
 * @param year - The year to process
 * @param currentYear - Current year
 * @param currentMonthIndex - Current month index (0-based)
 * @internal
 */
export function processPayeeInflows(inflowSection, transactionSummary, year, currentYear, currentMonthIndex) {
    // Initialize total inflow tracking
    const totalInflowByMonth = initializeMonthlyValues();
    // Track each payee's monthly inflows
    const payeeInflowMap = new Map();
    // Process payee totals from transaction summary
    for (const [payee, monthlyData] of Object.entries(transactionSummary.payeeMonthlyTotals)) {
        let hasInflowForYear = false;
        const payeeMonthly = initializeMonthlyValues();
        for (const [yearMonth, totals] of Object.entries(monthlyData)) {
            const [txYear, txMonth] = yearMonth.split('-').map(Number);
            if (txYear === year) {
                hasInflowForYear = true;
                const monthKey = MONTHS[txMonth - 1];
                payeeMonthly[monthKey] = totals.inflow;
                totalInflowByMonth[monthKey] += totals.inflow;
            }
        }
        if (hasInflowForYear) {
            payeeInflowMap.set(payee, payeeMonthly);
        }
    }
    // Build finalized inflow section
    inflowSection.total = finalizeSeries(totalInflowByMonth, year, currentYear, currentMonthIndex);
    inflowSection.payees = Array.from(payeeInflowMap.entries())
        .map(([payee, monthly]) => ({
        payee,
        monthly: finalizeSeries(monthly, year, currentYear, currentMonthIndex),
    }))
        .sort((a, b) => a.payee.localeCompare(b.payee));
}
/**
 * Processes category and bucket outflows from the transaction summary
 * @param outflowSection - Outflow section to populate
 * @param transactionSummary - Pre-computed transaction summary data
 * @param categoriesWithBuckets - Categories with their buckets
 * @param year - The year to process
 * @param currentYear - Current year
 * @param currentMonthIndex - Current month index (0-based)
 * @param bucketsMap - Map of all buckets including archived ones
 * @internal
 */
export function processCategoryAndBucketOutflows(outflowSection, transactionSummary, categoriesWithBuckets, year, currentYear, currentMonthIndex, bucketsMap) {
    // Initialize total outflow tracking
    const totalOutflowByMonth = initializeMonthlyValues();
    // Generate a map to quickly check if a bucket ID belongs to a known category
    const bucketToCategoryMap = new Map();
    categoriesWithBuckets.forEach((category) => {
        category.buckets.forEach((bucket) => {
            bucketToCategoryMap.set(bucket.id, category.id);
        });
    });
    // Process each regular category (non-archived buckets)
    const regularCategories = categoriesWithBuckets.map((category) => {
        const categoryRow = {
            categoryId: category.id,
            categoryName: category.name,
            monthly: {},
            buckets: [],
        };
        const categoryTotal = initializeMonthlyValues();
        // Process only non-archived buckets in this category
        const activeBuckets = category.buckets.filter((bucket) => !transactionSummary.archivedBucketIds.has(bucket.id));
        categoryRow.buckets = activeBuckets.map((bucket) => {
            const bucketMonthly = initializeMonthlyValues();
            const bucketData = transactionSummary.bucketMonthlyTotals[bucket.id];
            if (bucketData) {
                for (const [yearMonth, totals] of Object.entries(bucketData)) {
                    const [txYear, txMonth] = yearMonth.split('-').map(Number);
                    if (txYear === year) {
                        const monthKey = MONTHS[txMonth - 1];
                        bucketMonthly[monthKey] = totals.netExpense;
                        categoryTotal[monthKey] += totals.netExpense;
                        totalOutflowByMonth[monthKey] += totals.netExpense;
                    }
                }
            }
            return {
                bucketId: bucket.id,
                bucketName: bucket.name,
                monthly: finalizeSeries(bucketMonthly, year, currentYear, currentMonthIndex),
            };
        });
        // Finalize category totals
        categoryRow.monthly = finalizeSeries(categoryTotal, year, currentYear, currentMonthIndex);
        return categoryRow;
    });
    // Now handle special categories
    const specialCategories = [];
    // 1. Create Archived category with individual archived buckets
    if (transactionSummary.archivedBucketIds.size > 0) {
        const archivedBuckets = [];
        const archivedCategoryMonthly = initializeMonthlyValues();
        let hasArchivedData = false;
        // Process each archived bucket
        Array.from(transactionSummary.archivedBucketIds).forEach((bucketId) => {
            const bucketMonthly = initializeMonthlyValues();
            const bucketData = transactionSummary.bucketMonthlyTotals[bucketId];
            const bucket = bucketsMap.get(bucketId);
            if (bucketData) {
                let bucketHasData = false;
                for (const [yearMonth, totals] of Object.entries(bucketData)) {
                    const [txYear, txMonth] = yearMonth.split('-').map(Number);
                    if (txYear === year) {
                        bucketHasData = true;
                        hasArchivedData = true;
                        const monthKey = MONTHS[txMonth - 1];
                        bucketMonthly[monthKey] = totals.netExpense;
                        archivedCategoryMonthly[monthKey] += totals.netExpense;
                        totalOutflowByMonth[monthKey] += totals.netExpense;
                    }
                }
                // Only add buckets that have activity
                if (bucketHasData) {
                    archivedBuckets.push({
                        bucketId,
                        bucketName: bucket ? bucket.name : `Archived Bucket (${bucketId})`,
                        monthly: finalizeSeries(bucketMonthly, year, currentYear, currentMonthIndex),
                    });
                }
            }
        });
        // Add archived category if it has data
        if (hasArchivedData && archivedBuckets.length > 0) {
            specialCategories.push({
                categoryId: ARCHIVED_CATEGORY_ID,
                categoryName: 'Archived',
                monthly: finalizeSeries(archivedCategoryMonthly, year, currentYear, currentMonthIndex),
                buckets: archivedBuckets.sort((a, b) => a.bucketName.localeCompare(b.bucketName)),
            });
        }
    }
    // 2. Process uncategorized transactions if they exist
    if (transactionSummary.bucketMonthlyTotals[UNCATEGORIZED_BUCKET_CATEGORY_ID]) {
        const uncategorizedMonthly = initializeMonthlyValues();
        const uncategorizedData = transactionSummary.bucketMonthlyTotals[UNCATEGORIZED_BUCKET_CATEGORY_ID];
        let hasUncategorizedData = false;
        for (const [yearMonth, totals] of Object.entries(uncategorizedData)) {
            const [txYear, txMonth] = yearMonth.split('-').map(Number);
            if (txYear === year) {
                hasUncategorizedData = true;
                const monthKey = MONTHS[txMonth - 1];
                uncategorizedMonthly[monthKey] = totals.netExpense;
                totalOutflowByMonth[monthKey] += totals.netExpense;
            }
        }
        if (hasUncategorizedData) {
            const uncategorizedCategory = {
                categoryId: UNCATEGORIZED_BUCKET_CATEGORY_ID,
                categoryName: 'Uncategorized',
                monthly: finalizeSeries(uncategorizedMonthly, year, currentYear, currentMonthIndex),
                buckets: [
                    {
                        bucketId: UNCATEGORIZED_BUCKET_CATEGORY_ID,
                        bucketName: 'Uncategorized Expenses',
                        monthly: finalizeSeries(uncategorizedMonthly, year, currentYear, currentMonthIndex),
                    },
                ],
            };
            specialCategories.push(uncategorizedCategory);
        }
    }
    // Combine regular and special categories
    outflowSection.categories = [...regularCategories, ...specialCategories];
    // Finalize total outflow
    outflowSection.total = finalizeSeries(totalOutflowByMonth, year, currentYear, currentMonthIndex);
}
/**
 * Builds the summary section with net difference and net worth data
 * @param reportData - Report data structure to populate
 * @param year - The year being reported
 * @param currentYear - Current year
 * @param currentMonthIndex - Current month index (0-based)
 * @internal
 */
export function buildSummarySection(reportData, year, currentYear, currentMonthIndex) {
    let previousDifference = 0;
    for (let i = 0; i < MONTHS.length; i++) {
        const month = MONTHS[i];
        const status = determineMonthStatus(year, i, currentYear, currentMonthIndex);
        // Only process non-future months
        if (status !== MonthStatus.FUTURE) {
            const inflowValue = reportData.inflow.total[month].value;
            const outflowValue = reportData.outflow.total[month].value;
            const difference = inflowValue - outflowValue;
            // Calculate trends
            let differenceTrend = TrendType.NONE;
            if (i > 0) {
                differenceTrend = calculateTrend(difference, previousDifference);
            }
            reportData.summary[month] = {
                difference,
                differenceTrend,
                status,
            };
            // Store values for next iteration
            previousDifference = difference;
        }
    }
}
/**
 * Calculates the trend between current and previous values
 * @param current - Current value
 * @param previous - Previous value to compare against
 * @returns Trend type indicating relationship between values
 * @internal
 */
export function calculateTrend(current, previous) {
    if (current > previous) {
        return TrendType.HIGHER;
    }
    else if (current < previous) {
        return TrendType.LOWER;
    }
    else {
        return TrendType.NONE;
    }
}
/**
 * Determines the temporal status of a month
 * @param year - The year of the month
 * @param monthIndex - The index of the month (0-11)
 * @param currentYear - The current year
 * @param currentMonthIndex - The current month index (0-11)
 * @returns The temporal status of the month
 * @internal
 */
export function determineMonthStatus(year, monthIndex, currentYear, currentMonthIndex) {
    if (year < currentYear) {
        return MonthStatus.PAST;
    }
    else if (year > currentYear) {
        return MonthStatus.FUTURE;
    }
    else {
        // Same year
        if (monthIndex < currentMonthIndex) {
            return MonthStatus.PAST;
        }
        else if (monthIndex > currentMonthIndex) {
            return MonthStatus.FUTURE;
        }
        else {
            return MonthStatus.CURRENT;
        }
    }
}
/**
 * Finalizes raw monthly values into trends + status in one pass, excluding future months
 * @param raw - Plain monthly values
 * @param year - Report year
 * @param currentYear - Current year
 * @param currentMonthIndex - Current month index (0-based)
 * @returns Monthly trends with value, trend, and status for non-future months
 * @internal
 */
export function finalizeSeries(raw, year, currentYear, currentMonthIndex) {
    const result = {};
    let previousValue = 0;
    for (let i = 0; i < MONTHS.length; i++) {
        const month = MONTHS[i];
        const status = determineMonthStatus(year, i, currentYear, currentMonthIndex);
        // Only include non-future months
        if (status !== MonthStatus.FUTURE) {
            const value = raw[month];
            // Calculate trend only for non-first months
            let trend = TrendType.NONE;
            if (i > 0) {
                trend = calculateTrend(value, previousValue);
            }
            result[month] = { value, trend, status };
            previousValue = value;
        }
    }
    return result;
}
/**
 * Helper function to initialize monthly values with a default number
 * @param defaultValue - Default value to fill months with
 * @returns Object with zero values for each month
 * @internal
 */
export function initializeMonthlyValues(defaultValue = 0) {
    const result = {};
    for (const month of MONTHS) {
        result[month] = defaultValue;
    }
    return result;
}
/**
 * Initializes an empty inflow section with zero values
 * @param year - Report year
 * @param currentYear - Current year
 * @param currentMonthIndex - Current month index (0-based)
 * @internal
 */
export function initializeInflowSection(year, currentYear, currentMonthIndex) {
    const monthlyZeros = initializeMonthlyValues();
    const trends = finalizeSeries(monthlyZeros, year, currentYear, currentMonthIndex);
    return {
        total: trends,
        payees: [],
    };
}
/**
 * Initializes an empty outflow section with categories structure
 * @param year - Report year
 * @param currentYear - Current year
 * @param currentMonthIndex - Current month index (0-based)
 * @internal
 */
export function initializeOutflowSection(year, currentYear, currentMonthIndex) {
    const monthlyZeros = initializeMonthlyValues();
    const trends = finalizeSeries(monthlyZeros, year, currentYear, currentMonthIndex);
    return {
        total: trends,
        categories: [],
    };
}
/**
 * Initializes an empty summary section with zero values for non-future months only
 * @param year - Report year
 * @param currentYear - Current year
 * @param currentMonthIndex - Current month index (0-based)
 * @internal
 */
export function initializeSummarySection(year, currentYear, currentMonthIndex) {
    const summary = {};
    for (let i = 0; i < MONTHS.length; i++) {
        const month = MONTHS[i];
        const status = determineMonthStatus(year, i, currentYear, currentMonthIndex);
        // Only include non-future months
        if (status !== MonthStatus.FUTURE) {
            summary[month] = {
                difference: 0,
                differenceTrend: TrendType.NONE,
                status,
            };
        }
    }
    return summary;
}
/**
 * Filters categories by non-zero bucket activity and returns only categories with buckets that have expenses
 * @param categoryRow - Category row to filter
 * @param year - Report year
 * @param currentYear - Current year
 * @param currentMonthIndex - Current month index (0-based)
 * @returns Filtered category row or null if no activity
 * @internal
 */
export function filterCategoryByActivity(categoryRow, year, currentYear, currentMonthIndex) {
    // First, filter buckets to only those with non-zero values
    const activeBuckets = categoryRow.buckets.filter((bucket) => Object.values(bucket.monthly).some((month) => month.value !== 0));
    // If no active buckets, return null
    if (activeBuckets.length === 0) {
        return null;
    }
    // Create new category row with only active buckets
    return {
        ...categoryRow,
        buckets: activeBuckets,
        // Update category monthly values to reflect only active buckets
        monthly: recalculateCategoryMonthly(activeBuckets, year, currentYear, currentMonthIndex),
    };
}
